import * as React from 'react';
import { tokens } from "../../theme";
import { Button, useTheme, TextField, CircularProgress, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from "@mui/material";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Backdrop from '@mui/material/Backdrop';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';

function UpdatePortalModal({
    modalOpen,
    setModalOpen,
    studentUuid,
    selectedCourses,
    courseTitleCheckedMap,
    emailContent,
    author,
    selectedSemester,
    onSuccess,
    onError,
    handleLogout,
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 700,
        maxHeight: '80vh',
        bgcolor: colors.primary[600],
        border: '2px solid grey',
        boxShadow: 24,
        p: 4,
        overflowY: "auto",
    };

    const [loading, setLoading] = React.useState(false);
    const [checkingExisting, setCheckingExisting] = React.useState(false);
    const [courseFees, setCourseFees] = React.useState({});
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [validationErrors, setValidationErrors] = React.useState({});
    const [existingCourseIds, setExistingCourseIds] = React.useState([]);
    const [coursePolicies, setCoursePolicies] = React.useState([]);
    const [policiesLoading, setPoliciesLoading] = React.useState(true);

    // Fetch course policies from API
    React.useEffect(() => {
        const fetchPolicies = async () => {
            try {
                const response = await Axios.get(
                    `${process.env.REACT_APP_URL}/api/v1/course-policies`,
                    {
                        headers: {
                            Authorization: getAuthHeader(),
                        },
                    }
                );
                setCoursePolicies(response.data);
            } catch (error) {
                console.error("Error fetching course policies:", error);
                onError("Failed to load course policies");
            } finally {
                setPoliciesLoading(false);
            }
        };
        fetchPolicies();
    }, []);

    // Get selected course titles and IDs from courseTitleCheckedMap
    const selectedCourseDetails = React.useMemo(() => {
        const details = [];
        Object.keys(courseTitleCheckedMap).forEach(key => {
            if (courseTitleCheckedMap[key] === true) {
                const parts = key.split('|');
                const courseId = parts[parts.length - 1];
                const courseTitle = parts.slice(0, -1).join('|');

                // Skip combined courses (course_id === 'null')
                if (courseId !== 'null' && courseId) {
                    details.push({
                        course_id: parseInt(courseId),
                        course_title: courseTitle,
                    });
                }
            }
        });
        return details;
    }, [courseTitleCheckedMap]);

    // Initialize fees, start_date, policy_id, custom_policy_text, and custom_policy_text_kr for each course
    React.useEffect(() => {
        if (policiesLoading || coursePolicies.length === 0) return;

        const initialFees = {};
        selectedCourseDetails.forEach(course => {
            if (!courseFees[course.course_id]) {
                const defaultPolicy = coursePolicies.find(p => p.id === 1) || coursePolicies[0];
                initialFees[course.course_id] = {
                    tuition_fee: '',
                    textbook_fee: '',
                    exam_fee: '',
                    start_date: '',
                    policy_id: defaultPolicy?.id || 1, // Default to first policy
                    custom_policy_text: defaultPolicy?.description || '',
                    custom_policy_text_kr: defaultPolicy?.description_kr || '',
                };
            }
        });
        if (Object.keys(initialFees).length > 0) {
            setCourseFees(prev => ({ ...prev, ...initialFees }));
        }
    }, [selectedCourseDetails, coursePolicies, policiesLoading]);

    const handleFeeChange = (courseId, feeType, value) => {
        // Allow empty string or valid decimal numbers
        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
            setCourseFees(prev => ({
                ...prev,
                [courseId]: {
                    ...prev[courseId],
                    [feeType]: value,
                },
            }));
        }
    };

    const handleDateChange = (courseId, value) => {
        setCourseFees(prev => ({
            ...prev,
            [courseId]: {
                ...prev[courseId],
                start_date: value,
            },
        }));
    };

    const handlePolicyChange = (courseId, policyId) => {
        const policy = coursePolicies.find(p => p.id === parseInt(policyId, 10));
        setCourseFees(prev => ({
            ...prev,
            [courseId]: {
                ...prev[courseId],
                policy_id: parseInt(policyId, 10),
                custom_policy_text: policy?.description || '',
                custom_policy_text_kr: policy?.description_kr || '',
            },
        }));
    };

    const handlePolicyTextChange = (courseId, text) => {
        setCourseFees(prev => ({
            ...prev,
            [courseId]: {
                ...prev[courseId],
                custom_policy_text: text,
            },
        }));
    };

    const handlePolicyTextKrChange = (courseId, text) => {
        setCourseFees(prev => ({
            ...prev,
            [courseId]: {
                ...prev[courseId],
                custom_policy_text_kr: text,
            },
        }));
    };

    const handleResetPolicyText = (courseId) => {
        const policyId = courseFees[courseId]?.policy_id || 1;
        const policy = coursePolicies.find(p => p.id === policyId);
        setCourseFees(prev => ({
            ...prev,
            [courseId]: {
                ...prev[courseId],
                custom_policy_text: policy?.description || '',
            },
        }));
    };

    const handleResetPolicyTextKr = (courseId) => {
        const policyId = courseFees[courseId]?.policy_id || 1;
        const policy = coursePolicies.find(p => p.id === policyId);
        setCourseFees(prev => ({
            ...prev,
            [courseId]: {
                ...prev[courseId],
                custom_policy_text_kr: policy?.description_kr || '',
            },
        }));
    };

    const handleModalClose = () => {
        if (!loading) {
            setModalOpen(false);
            setShowConfirmation(false);
            setValidationErrors({});
        }
    };

    const validateFees = () => {
        const errors = {};
        let hasErrors = false;

        selectedCourseDetails.forEach(course => {
            const fees = courseFees[course.course_id] || {};
            const courseErrors = {};

            if (!fees.tuition_fee || fees.tuition_fee.trim() === '') {
                courseErrors.tuition_fee = true;
                hasErrors = true;
            }
            if (!fees.textbook_fee || fees.textbook_fee.trim() === '') {
                courseErrors.textbook_fee = true;
                hasErrors = true;
            }

            if (Object.keys(courseErrors).length > 0) {
                errors[course.course_id] = courseErrors;
            }
        });

        setValidationErrors(errors);
        return !hasErrors;
    };

    const handleProceedToConfirmation = async () => {
        if (selectedCourseDetails.length === 0) {
            onError("No courses selected!");
            return;
        }

        if (!emailContent || emailContent.trim() === '') {
            onError("Email content is required!");
            return;
        }

        if (!selectedSemester) {
            onError("Please select a semester!");
            return;
        }

        if (!validateFees()) {
            onError("Please fill in Tuition Fee and Textbook Fee for all courses!");
            return;
        }

        // Check for existing records on parent portal
        setCheckingExisting(true);
        try {
            const courseIds = selectedCourseDetails.map(c => c.course_id);
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/students/${studentUuid}/check-portal-visibility`,
                { course_ids: courseIds },
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );
            setExistingCourseIds(response.data.existing_course_ids || []);
        } catch (error) {
            console.error("Error checking existing records:", error);
            // Continue anyway, just won't show warnings
            setExistingCourseIds([]);
        } finally {
            setCheckingExisting(false);
        }

        setShowConfirmation(true);
    };

    const handleBackToEdit = () => {
        setShowConfirmation(false);
    };

    // Get warnings for a course's fees
    const getFeeWarnings = (courseId) => {
        const fees = courseFees[courseId] || {};
        const tuition = parseFloat(fees.tuition_fee) || 0;
        const textbook = parseFloat(fees.textbook_fee) || 0;
        const warnings = [];

        if (tuition < 1000) {
            warnings.push(`Tuition Fee is $${tuition.toFixed(2)} (less than $1,000). Is this amount correct?`);
        }
        if (textbook < 50) {
            warnings.push(`Textbook Fee is $${textbook.toFixed(2)} (less than $50). Is this amount correct?`);
        } else if (textbook > 200) {
            warnings.push(`Textbook Fee is $${textbook.toFixed(2)} (more than $200). Is this amount correct?`);
        }

        return warnings;
    };

    // Check if any course has warnings
    const hasAnyWarnings = React.useMemo(() => {
        return selectedCourseDetails.some(course => getFeeWarnings(course.course_id).length > 0);
    }, [selectedCourseDetails, courseFees]);

    const handleUpdatePortal = async () => {
        setLoading(true);

        try {
            const coursesData = selectedCourseDetails.map(course => ({
                course_id: course.course_id,
                tuition_fee: courseFees[course.course_id]?.tuition_fee || null,
                textbook_fee: courseFees[course.course_id]?.textbook_fee || null,
                exam_fee: courseFees[course.course_id]?.exam_fee || null,
                start_date: courseFees[course.course_id]?.start_date || null,
                policy_id: courseFees[course.course_id]?.policy_id || 1,
                custom_policy_text: courseFees[course.course_id]?.custom_policy_text || null,
                custom_policy_text_kr: courseFees[course.course_id]?.custom_policy_text_kr || null,
            }));

            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/students/${studentUuid}/update-portal-visibility`,
                {
                    courses: coursesData,
                    email_content: emailContent,
                    semester_id: selectedSemester,
                    author: author || null,
                },
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );

            if (response.status === 200) {
                onSuccess(`Successfully updated ${response.data.created} new and ${response.data.updated} existing courses to parent portal`);
                // Reset state before closing so next open starts fresh
                setShowConfirmation(false);
                setCourseFees({});
                setValidationErrors({});
                setExistingCourseIds([]);
                setModalOpen(false);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
                return;
            }
            onError(error.response?.data?.error || error.response?.data?.message || "Failed to update portal");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={modalOpen}
            onClose={handleModalClose}
            aria-labelledby="update-portal-modal-title"
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: 500,
                },
            }}
        >
            <Fade in={modalOpen}>
                <Box sx={style}>
                    <Typography variant="h3" mb={3} borderBottom={1} pb={1}>
                        Update to Parent Portal
                    </Typography>

                    {policiesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : selectedCourseDetails.length === 0 ? (
                        <Typography variant="h6" color="error" textAlign="center" py={4}>
                            No courses selected. Please select at least one course.
                        </Typography>
                    ) : !showConfirmation ? (
                        /* Step 1: Fee Input Form */
                        <>
                            <Typography variant="h6" mb={2} color={colors.grey[300]}>
                                Enter Fees for Selected Courses ({selectedCourseDetails.length})
                            </Typography>

                            <Box sx={{ maxHeight: '400px', overflowY: 'auto', mb: 3 }}>
                                {selectedCourseDetails.map((course, index) => (
                                    <Box
                                        key={course.course_id}
                                        sx={{
                                            border: `1px solid ${colors.grey[700]}`,
                                            borderRadius: 1,
                                            p: 2,
                                            mb: 2,
                                        }}
                                    >
                                        <Typography variant="h6" mb={2} fontWeight="bold">
                                            {index + 1}. {course.course_title}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                            <TextField
                                                label="Tuition Fee ($) *"
                                                size="small"
                                                value={courseFees[course.course_id]?.tuition_fee || ''}
                                                onChange={(e) => handleFeeChange(course.course_id, 'tuition_fee', e.target.value)}
                                                sx={{ width: 150 }}
                                                placeholder="0.00"
                                                error={validationErrors[course.course_id]?.tuition_fee}
                                                required
                                            />
                                            <TextField
                                                label="Textbook Fee ($) *"
                                                size="small"
                                                value={courseFees[course.course_id]?.textbook_fee || ''}
                                                onChange={(e) => handleFeeChange(course.course_id, 'textbook_fee', e.target.value)}
                                                sx={{ width: 150 }}
                                                placeholder="0.00"
                                                error={validationErrors[course.course_id]?.textbook_fee}
                                                required
                                            />
                                            <TextField
                                                label="Exam Fee ($)"
                                                size="small"
                                                value={courseFees[course.course_id]?.exam_fee || ''}
                                                onChange={(e) => handleFeeChange(course.course_id, 'exam_fee', e.target.value)}
                                                sx={{ width: 150 }}
                                                placeholder="0.00"
                                            />
                                            <TextField
                                                label="Start Date"
                                                type="date"
                                                size="small"
                                                value={courseFees[course.course_id]?.start_date || ''}
                                                onChange={(e) => handleDateChange(course.course_id, e.target.value)}
                                                sx={{ width: 170 }}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Box>
                                        <FormControl component="fieldset" size="small">
                                            <FormLabel component="legend" sx={{ fontSize: '0.75rem', color: colors.grey[300] }}>
                                                Policy Type *
                                            </FormLabel>
                                            <RadioGroup
                                                row
                                                value={courseFees[course.course_id]?.policy_id || 1}
                                                onChange={(e) => handlePolicyChange(course.course_id, e.target.value)}
                                            >
                                                {coursePolicies.map((policy) => (
                                                    <FormControlLabel
                                                        key={policy.id}
                                                        value={policy.id}
                                                        control={<Radio size="small" />}
                                                        label={policy.display_name}
                                                        sx={{
                                                            '& .MuiFormControlLabel-label': {
                                                                fontSize: '0.875rem',
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                        <Box sx={{ mt: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                <FormLabel sx={{ fontSize: '0.75rem', color: colors.grey[300] }}>
                                                    Policy Text - English (editable)
                                                </FormLabel>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => handleResetPolicyText(course.course_id)}
                                                    sx={{ fontSize: '0.7rem', textTransform: 'none', color: colors.blueAccent[400] }}
                                                >
                                                    Reset to Default
                                                </Button>
                                            </Box>
                                            <TextField
                                                multiline
                                                rows={6}
                                                fullWidth
                                                size="small"
                                                value={courseFees[course.course_id]?.custom_policy_text || ''}
                                                onChange={(e) => handlePolicyTextChange(course.course_id, e.target.value)}
                                                placeholder="Enter policy text for this course..."
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        fontSize: '0.8rem',
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ mt: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                <FormLabel sx={{ fontSize: '0.75rem', color: colors.grey[300] }}>
                                                    Policy Text - Korean (editable)
                                                </FormLabel>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => handleResetPolicyTextKr(course.course_id)}
                                                    sx={{ fontSize: '0.7rem', textTransform: 'none', color: colors.blueAccent[400] }}
                                                >
                                                    Reset to Default
                                                </Button>
                                            </Box>
                                            <TextField
                                                multiline
                                                rows={6}
                                                fullWidth
                                                size="small"
                                                value={courseFees[course.course_id]?.custom_policy_text_kr || ''}
                                                onChange={(e) => handlePolicyTextKrChange(course.course_id, e.target.value)}
                                                placeholder="이 수업의 정책 내용을 입력하세요..."
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        fontSize: '0.8rem',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>

                            <Typography variant="body2" color={colors.grey[400]} mb={3}>
                                * Tuition Fee and Textbook Fee are required for all courses.
                            </Typography>
                        </>
                    ) : (
                        /* Step 2: Confirmation Summary */
                        <>
                            {existingCourseIds.length > 0 && (
                                <Box
                                    sx={{
                                        backgroundColor: '#e3f2fd',
                                        border: `2px solid #2196f3`,
                                        p: 2,
                                        borderRadius: 1,
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                    }}
                                >
                                    <InfoIcon sx={{ color: '#1565c0', mt: 0.5 }} />
                                    <Box>
                                        <Typography variant="h6" sx={{ color: '#1565c0' }} fontWeight="bold" mb={0.5}>
                                            {existingCourseIds.length} course{existingCourseIds.length > 1 ? 's' : ''} already exist{existingCourseIds.length === 1 ? 's' : ''} on Parent Portal
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1565c0' }}>
                                            Courses marked with "UPDATE" will have their fees and start date updated.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {hasAnyWarnings && (
                                <Box
                                    sx={{
                                        backgroundColor: '#ff9800',
                                        border: `2px solid #e65100`,
                                        p: 2,
                                        borderRadius: 1,
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                    }}
                                >
                                    <WarningAmberIcon sx={{ color: '#000', mt: 0.5 }} />
                                    <Box>
                                        <Typography variant="h6" sx={{ color: '#000' }} fontWeight="bold" mb={0.5}>
                                            Warning: Please Review Fee Amounts
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#000' }}>
                                            Some fee amounts may need verification. Please check the warnings below.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            <Box
                                sx={{
                                    backgroundColor: colors.greenAccent[900],
                                    border: `1px solid ${colors.greenAccent[700]}`,
                                    p: 2,
                                    borderRadius: 1,
                                    mb: 3,
                                }}
                            >
                                <Typography variant="h6" color={colors.greenAccent[400]} fontWeight="bold" mb={1}>
                                    Please Review Before Submitting
                                </Typography>
                                <Typography variant="body2" color={colors.grey[300]}>
                                    The following information will be visible to parents on the parent portal.
                                </Typography>
                            </Box>

                            <Typography variant="h6" mb={2} color={colors.grey[300]}>
                                Courses Summary ({selectedCourseDetails.length})
                            </Typography>

                            <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 3 }}>
                                {selectedCourseDetails.map((course, index) => {
                                    const fees = courseFees[course.course_id] || {};
                                    const tuition = parseFloat(fees.tuition_fee) || 0;
                                    const textbook = parseFloat(fees.textbook_fee) || 0;
                                    const exam = parseFloat(fees.exam_fee) || 0;
                                    const total = tuition + textbook + exam;
                                    const startDate = fees.start_date || null;
                                    const warnings = getFeeWarnings(course.course_id);
                                    const isExisting = existingCourseIds.includes(course.course_id);

                                    return (
                                        <Box
                                            key={course.course_id}
                                            sx={{
                                                border: warnings.length > 0 ? '2px solid #e65100' : isExisting ? '2px solid #2196f3' : `1px solid ${colors.grey[700]}`,
                                                borderRadius: 1,
                                                p: 2,
                                                mb: 2,
                                                backgroundColor: colors.primary[700],
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {index + 1}. {course.course_title}
                                                </Typography>
                                                {isExisting && (
                                                    <Box sx={{
                                                        backgroundColor: '#2196f3',
                                                        color: '#fff',
                                                        px: 1,
                                                        py: 0.25,
                                                        borderRadius: 1,
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                    }}>
                                                        UPDATE
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                                                <Typography variant="body2" color={colors.grey[300]}>
                                                    Tuition: <strong>${tuition.toFixed(2)}</strong>
                                                </Typography>
                                                <Typography variant="body2" color={colors.grey[300]}>
                                                    Textbook: <strong>${textbook.toFixed(2)}</strong>
                                                </Typography>
                                                {exam > 0 && (
                                                    <Typography variant="body2" color={colors.grey[300]}>
                                                        Exam: <strong>${exam.toFixed(2)}</strong>
                                                    </Typography>
                                                )}
                                                <Typography variant="body2" color={colors.blueAccent[400]} fontWeight="bold">
                                                    Total: ${total.toFixed(2)}
                                                </Typography>
                                                {startDate && (
                                                    <Typography variant="body2" color={colors.greenAccent[400]}>
                                                        Start: <strong>{startDate}</strong>
                                                    </Typography>
                                                )}
                                                <Typography variant="body2" color={colors.grey[300]}>
                                                    Policy: <strong>{coursePolicies.find(p => p.id === (fees.policy_id || 1))?.display_name || 'Group'}</strong>
                                                </Typography>
                                            </Box>
                                            {warnings.length > 0 && (
                                                <Box sx={{
                                                    mt: 1.5,
                                                    p: 1.5,
                                                    backgroundColor: '#ff9800',
                                                    borderRadius: 1,
                                                }}>
                                                    {warnings.map((warning, wIndex) => (
                                                        <Box key={wIndex} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: wIndex < warnings.length - 1 ? 0.5 : 0 }}>
                                                            <WarningAmberIcon sx={{ color: '#000', fontSize: 18 }} />
                                                            <Typography variant="body2" sx={{ color: '#000', fontWeight: 'bold' }}>
                                                                {warning}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>

                            <Box
                                sx={{
                                    backgroundColor: colors.primary[700],
                                    p: 2,
                                    borderRadius: 1,
                                    mb: 3,
                                }}
                            >
                                <Typography variant="body2" color={colors.grey[300]} mb={1}>
                                    <strong>Grand Total:</strong> ${selectedCourseDetails.reduce((sum, course) => {
                                        const fees = courseFees[course.course_id] || {};
                                        return sum + (parseFloat(fees.tuition_fee) || 0) + (parseFloat(fees.textbook_fee) || 0) + (parseFloat(fees.exam_fee) || 0);
                                    }, 0).toFixed(2)}
                                </Typography>
                                <Typography variant="body2" color={colors.grey[400]}>
                                    Parents will see the email content and these courses with fees on the parent portal.
                                </Typography>
                            </Box>
                        </>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        {showConfirmation && (
                            <Button
                                variant="outlined"
                                onClick={handleBackToEdit}
                                disabled={loading}
                            >
                                Back to Edit
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            onClick={handleModalClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        {!showConfirmation ? (
                            <Button
                                variant="contained"
                                onClick={handleProceedToConfirmation}
                                disabled={selectedCourseDetails.length === 0 || checkingExisting}
                                sx={{
                                    backgroundColor: colors.blueAccent[600],
                                    '&:hover': {
                                        backgroundColor: colors.blueAccent[500],
                                    },
                                }}
                            >
                                {checkingExisting ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    'Review & Confirm'
                                )}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleUpdatePortal}
                                disabled={loading}
                                sx={{
                                    backgroundColor: colors.greenAccent[600],
                                    '&:hover': {
                                        backgroundColor: colors.greenAccent[500],
                                    },
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    'Confirm & Update'
                                )}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
}

export default UpdatePortalModal;
